/**
 * Estado de un vehículo — fuente única de la app.
 *
 * Hay DOS estados, y no son lo mismo:
 *
 *   - MOVIMIENTO (telemetría): lo que el equipo GPS reporta solo.
 *     `moving` · `stopped` · `parked` · `disconnected`.
 *   - ADMINISTRATIVO (`vehicles.status`): lo que una persona declaró sobre el
 *     vehículo desde /admin. `active` (operativo) · `inactive` ·
 *     `maintenance` · `stolen`.
 *
 * El administrativo GANA cuando no es `active`, y esa es la razón de ser de
 * este módulo: una moto en el taller se ve exactamente igual que una robada si
 * solo se mira la telemetría — las dos están quietas y sin reportar — y la app
 * las pintaba a ambas como "Desconectado" en rojo, que es la etiqueta de un
 * problema que nadie tiene. Lo mismo con "Estacionado": es un estado normal
 * que no dice nada de un vehículo que su dueño dio de baja.
 *
 * El estado de movimiento NO se pierde al ser sobrescrito: viaja aparte en
 * `motionState`/`motionLabel`. Importa sobre todo con un vehículo robado —
 * "Robado" es el titular, pero "En movimiento 45 km/h" es el dato con el que
 * se actúa — y por eso la ficha y la lista siguen mostrando velocidad y
 * ubicación de un vehículo fuera de servicio.
 */

/**
 * Estados administrativos que sacan al vehículo de la operación, en orden de
 * gravedad. `active` (y la ausencia de vehículo, p. ej. un equipo sin vehículo
 * asignado) NO está acá: es la operación normal y deja mandar a la telemetría.
 */
export const OUT_OF_SERVICE_STATES = Object.freeze([
  "stolen",
  "maintenance",
  "inactive",
]);

const OUT_OF_SERVICE_SET = new Set(OUT_OF_SERVICE_STATES);

/**
 * Estados administrativos que sacan al vehículo de TODA la app salvo su ficha
 * en /admin → Vehículos: no aparece en el Monitor, en los selectores ni en las
 * alertas. Hoy es solo `stolen` — un vehículo robado es un equipo que ya no
 * hace nada útil. Espejo de `HIDDEN_VEHICLE_STATUSES` en
 * `backend/src/lib/vehicleStatus.js`, que es quien lo filtra en origen.
 */
export const HIDDEN_VEHICLE_STATUSES = Object.freeze(["stolen"]);

/**
 * ¿El vehículo con este estado administrativo se oculta de la app?
 *
 * @param {string|null|undefined} status - `vehicle_status` del device
 * @returns {boolean}
 */
export function isHiddenVehicleStatus(status) {
  return HIDDEN_VEHICLE_STATUSES.includes(status);
}

/**
 * Los cuatro valores de `vehicles.status`, en el orden en que se ofrecen: del
 * más común al más excepcional. Es la lista que pintan el formulario y el
 * filtro de /admin, para que agregar un estado sea un cambio en un solo lugar
 * (más la migración que amplíe el CHECK de la tabla).
 */
export const VEHICLE_STATUSES = Object.freeze([
  "active",
  "maintenance",
  "inactive",
  "stolen",
]);

/** Etiqueta de cada estado administrativo. Misma palabra que usa /admin. */
export const VEHICLE_STATUS_LABEL = Object.freeze({
  active: "Activo",
  inactive: "Inactivo",
  maintenance: "Mantenimiento",
  stolen: "Robado",
});

/**
 * Qué le pasa a las alertas de un vehículo en cada estado administrativo.
 *
 * Es el contrato del backend dicho en una línea (`lib/vehicleStatus.js`), para
 * ponerlo como `title` donde alguien elige vehículos para configurar alertas o
 * asignar geocercas y necesita saber qué va a recibir.
 */
export const VEHICLE_STATUS_ALERT_HINT = Object.freeze({
  inactive:
    "Fuera de operación: no se emiten sus alertas operativas (motor, estacionado, sin conexión, batería). Seguridad y geocercas siguen activas.",
  maintenance:
    "Fuera de operación: no se emiten sus alertas operativas (motor, estacionado, sin conexión, batería). Seguridad y geocercas siguen activas.",
  stolen:
    "Robado: se emiten TODAS sus alertas y salen con urgencia máxima, por encima de lo configurado.",
});

/**
 * Icono (Material Symbols) de cada estado, para las listas.
 *
 * `disconnected` NO tiene entrada a propósito: su punto rojo apagado ya lo dice
 * todo y un icono al lado sería la misma frase dos veces. Un estado sin icono
 * cae en `undefined` y el consumidor no dibuja nada.
 */
export const STATE_ICON = Object.freeze({
  moving: "navigation",
  parked: "local_parking",
  stopped: "stop_circle",
  maintenance: "build",
  stolen: "e911_emergency",
  inactive: "do_not_disturb_on",
});

/**
 * Etiqueta ESTABLE de cada estado — sin la velocidad, que cambia con cada
 * reporte.
 *
 * Es la que va en `aria-label` y en los tooltips: con la etiqueta dinámica, el
 * nombre accesible del icono se reescribía cada ~20 s y el lector de pantalla
 * volvía a anunciar la fila entera. El texto con velocidad sigue existiendo
 * como `label` para lo que se lee una vez (la insignia de la ficha).
 */
export const STATE_LABEL = Object.freeze({
  moving: "En movimiento",
  stopped: "Detenido",
  parked: "Estacionado",
  disconnected: "Desconectado",
  maintenance: "Mantenimiento",
  stolen: "Robado",
  inactive: "Inactivo",
});

/**
 * Whether the user perceives this device as "connected" (Online badge).
 * Combines both signals so the stat counter and filter agree with the badge:
 *   - `tcp_connected` (live socket within last 2 min) → connected
 *   - `status === 'online'` (heartbeat within last hour, but socket may be
 *     mid-cycle) → connected
 *
 * Es una lectura del EQUIPO, no del vehículo: el estado administrativo no la
 * toca. La ficha de un GPS tiene que poder decir "el equipo sigue reportando"
 * aunque su vehículo esté marcado como robado.
 *
 * @param {{ tcp_connected?: boolean, status?: string }} device
 * @returns {boolean}
 */
export function isDeviceConnected(device) {
  if (!device) return false;
  return device.tcp_connected === true || device.status === "online";
}

/**
 * ¿Este `vehicles.status` saca al vehículo de la operación?
 *
 * Tolerante a lo que de verdad llega: `null`/`undefined` (equipo sin vehículo
 * asignado, o cliente viejo cacheado antes de que la ruta sirviera el campo) y
 * cualquier valor desconocido cuentan como operativos. La regla es
 * fail-operational a propósito: ante la duda, mostrar la telemetría real es
 * menos malo que declarar fuera de servicio un vehículo que está trabajando.
 *
 * @param {string|null|undefined} vehicleStatus
 * @returns {boolean}
 */
export function isOutOfServiceStatus(vehicleStatus) {
  return OUT_OF_SERVICE_SET.has(vehicleStatus);
}

/** Igual que `isOutOfServiceStatus`, pero sobre el objeto device. */
export function isOutOfService(device) {
  return isOutOfServiceStatus(device?.vehicle_status);
}

/**
 * Estado de movimiento puro, como STRING — solo telemetría, sin mirar el
 * administrativo y sin construir ningún objeto.
 *
 * Existe aparte de `getMotionState` porque los tres bucles calientes de la app
 * (el recálculo del cúmulo, los contadores incrementales del store y el filtro
 * de la lista) recorren la flota entera y solo necesitan la palabra. Con el
 * objeto intermedio pagaban una asignación por vehículo por pasada, que es
 * justo el coste que las fases 2 y 4 se dedicaron a sacar del camino.
 *
 * @param {object} device
 * @returns {'moving'|'stopped'|'parked'|'disconnected'}
 */
export function motionStateOf(device) {
  // 'unknown' cuenta como desconectado ACÁ a propósito, aunque en el backend
  // sea un estado distinto y la página de Equipos lo muestre aparte ("Sin
  // contacto").
  //
  // Es una decisión conservadora, no un olvido: la paleta del mapa está fijada
  // en CLAUDE.md (azul en movimiento · amber detenido/estacionado · rojo
  // desconectado · violeta taller · rojo+pulso robado · gris inactivo) y no
  // tiene un color libre para un cuarto estado operativo — amber ya significa
  // "detenido", que es un estado NORMAL. Si 'unknown' cayera al `return
  // "stopped"` de abajo, un equipo que dejó de reportar se pintaría como
  // estacionado tranquilo, que miente más que pintarlo desconectado y además
  // lo sacaría del contador que el operador mira.
  //
  // Darle color propio en el mapa es una decisión de producto aparte; lo que
  // este cambio resuelve es el RUIDO DE ALERTAS, y eso vive en el backend.
  if (!device || device.status === "offline" || device.status === "unknown") {
    return "disconnected";
  }
  if (device.movement_status === "moving") return "moving";
  if (device.movement_status === "parked") return "parked";
  return "stopped";
}

/**
 * Estado EFECTIVO como string: el administrativo si lo sacaron de operación,
 * el de movimiento si no. Misma decisión que `getDeviceState`, sin el objeto.
 *
 * @param {object} device
 * @returns {string}
 */
export function effectiveStateOf(device) {
  const vehicleStatus = device?.vehicle_status;
  return isOutOfServiceStatus(vehicleStatus)
    ? vehicleStatus
    : motionStateOf(device);
}

/** Cubeta de contador/chip de un device, sin objetos intermedios. */
export function deviceChipKey(device) {
  return deviceStateToChipKey(effectiveStateOf(device));
}

/**
 * Estado de movimiento con su etiqueta legible (la de `moving` lleva la
 * velocidad, así que se arma en cada llamada).
 *
 * @param {object} device
 * @returns {{ state: 'moving'|'stopped'|'parked'|'disconnected', label: string }}
 */
export function getMotionState(device) {
  const state = motionStateOf(device);
  return {
    state,
    label:
      state === "moving"
        ? `En movimiento ${Math.round(device.speed ?? 0)} km/h`
        : STATE_LABEL[state],
  };
}

/**
 * Estado EFECTIVO del vehículo: el administrativo si lo sacaron de operación,
 * el de movimiento si no.
 *
 * @param {object} device - device del store de flota (`vehicle_status` opcional)
 * @returns {{
 *   state: 'moving'|'stopped'|'parked'|'disconnected'|'maintenance'|'stolen'|'inactive',
 *   label: string,
 *   adminState: 'maintenance'|'stolen'|'inactive'|null,
 *   motionState: 'moving'|'stopped'|'parked'|'disconnected',
 *   motionLabel: string,
 * }}
 */
export function getDeviceState(device) {
  const motion = getMotionState(device);
  const vehicleStatus = device?.vehicle_status;

  if (isOutOfServiceStatus(vehicleStatus)) {
    return {
      state: vehicleStatus,
      label: VEHICLE_STATUS_LABEL[vehicleStatus],
      adminState: vehicleStatus,
      motionState: motion.state,
      motionLabel: motion.label,
    };
  }

  return {
    state: motion.state,
    label: motion.label,
    adminState: null,
    motionState: motion.state,
    motionLabel: motion.label,
  };
}

/**
 * Bucket de chip/contador de un estado efectivo.
 *
 * `stopped` y `parked` colapsan en uno solo, y los tres administrativos en
 * `out_of_service`: sacarlos de los contadores operativos es la mitad del
 * punto — un taller lleno no puede seguir inflando "Desconectados", que es el
 * número que se mira para salir a buscar un equipo caído.
 *
 * @param {string} state
 * @returns {'moving'|'stopped'|'disconnected'|'out_of_service'}
 */
export function deviceStateToChipKey(state) {
  if (state === "moving") return "moving";
  if (state === "disconnected") return "disconnected";
  if (OUT_OF_SERVICE_SET.has(state)) return "out_of_service";
  return "stopped"; // cubre `stopped` y `parked`
}
